import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Flag } from "@phosphor-icons/react";
import { Reveal } from "./Reveal";
import { Surface } from "./Surface";

/* Пять выгод: асимметричная сетка, у каждой карточки своя небольшая живая схема.
   Схемы показывают смысл выгоды, цифр, людей и клиентов в них нет. */
const LOOP = { duration: 5, repeat: Infinity, ease: "easeInOut" };

const FeelToNumber = ({ reduce }) => (
  <div className="flex flex-wrap items-center gap-3" aria-hidden="true">
    <span className="relative rounded-full border border-ink-4 px-4 py-2 text-sm text-soft">
      «вроде много»
      <motion.i
        className="absolute inset-x-3 top-1/2 h-px origin-left bg-soft"
        initial={{ scaleX: reduce ? 1 : 0 }}
        animate={reduce ? undefined : { scaleX: [0, 1, 1, 0] }}
        transition={{ ...LOOP, times: [0, 0.3, 0.85, 1] }}
      />
    </span>
    <ArrowRight size={18} className="text-accent" />
    <motion.span
      className="rounded-full border border-accent bg-accent/10 px-4 py-2 font-mono text-sm text-accent-soft shadow-ring"
      animate={reduce ? undefined : { opacity: [0.45, 1, 1, 0.45] }}
      transition={{ ...LOOP, times: [0, 0.3, 0.85, 1] }}
    >
      часы и рубли
    </motion.span>
  </div>
);

const OneQuestion = ({ reduce }) => (
  <div className="flex items-center gap-3" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <motion.span
        key={i}
        className={`h-3 w-3 rounded-full ${i === 0 ? "bg-accent-soft shadow-[0_0_16px_rgba(62,139,255,1)]" : "bg-ink-4"}`}
        animate={i === 0 && !reduce ? { scale: [1, 1.5, 1] } : undefined}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      />
    ))}
    <span className="eyebrow ml-2">один вопрос</span>
  </div>
);

const Metric = ({ reduce }) => (
  <div aria-hidden="true">
    <div className="relative h-1.5 rounded-full bg-ink-3">
      <motion.div
        className="h-full rounded-full bg-gradient-to-r from-accent to-accent-cyan"
        initial={{ width: reduce ? "100%" : "0%" }}
        animate={reduce ? undefined : { width: ["0%", "100%", "100%", "0%"] }}
        transition={{ ...LOOP, times: [0, 0.55, 0.85, 1] }}
      />
      <Flag size={18} weight="fill" className="absolute -right-1 -top-6 text-accent-soft" />
    </div>
    <p className="eyebrow mt-3">метрика до старта</p>
  </div>
);

const Toggle = ({ on, label }) => (
  <div className="flex items-center gap-3">
    <span
      className={`relative h-6 w-11 rounded-full border transition-colors duration-500 ${
        on ? "border-accent bg-accent/30 shadow-ring" : "border-ink-4 bg-ink-3"
      }`}
    >
      <span
        className={`absolute top-0.5 rounded-full transition-all duration-500 ease-smooth ${
          on ? "left-5 bg-accent-soft" : "left-0.5 bg-soft"
        }`}
        style={{ height: 18, width: 18 }}
      />
    </span>
    <span className={`text-sm ${on ? "text-white" : "text-soft"}`}>{label}</span>
  </div>
);

const AiOnlyWhere = () => (
  <div className="flex flex-col gap-3" aria-hidden="true">
    <Toggle on label="здесь нужен ИИ" />
    <Toggle on={false} label="здесь хватит порядка" />
  </div>
);

const Author = () => (
  <div className="flex items-center gap-4" aria-hidden="true">
    <span className="relative">
      <span className="absolute -inset-1 rounded-full bg-gradient-to-br from-accent to-accent-cyan opacity-70 blur-md" />
      <img src="/author.jpg" alt="" width="56" height="56" loading="lazy" className="relative h-14 w-14 rounded-full object-cover" />
    </span>
    <span className="eyebrow">напрямую, без менеджера</span>
  </div>
);

export const Benefits = () => {
  const reduce = useReducedMotion();
  const cards = [
    {
      span: "md:col-span-7",
      big: true,
      title: "Цифра вместо ощущения",
      text: "Считаю, сколько часов и денег в год съедает ручная часть процесса.",
      visual: <FeelToNumber reduce={reduce} />,
    },
    {
      span: "md:col-span-5",
      big: true,
      title: "Первый шаг, который не страшно сделать",
      text: "Беру один вопрос и закрываю его целиком. Платформу не строю, поэтому цена ошибки остаётся маленькой.",
      visual: <OneQuestion reduce={reduce} />,
    },
    {
      span: "md:col-span-4",
      title: "Понятно, чем мерить успех",
      text: "Метрику согласуем до старта, чтобы «вроде стало лучше» не приходилось принимать на веру.",
      visual: <Metric reduce={reduce} />,
    },
    {
      span: "md:col-span-4",
      title: "ИИ только там, где он нужен",
      text: "В карте процесса есть вопрос, нужен ли здесь ИИ вообще. Если хватит обычной автоматизации, так и скажу.",
      visual: <AiOnlyWhere />,
    },
    {
      span: "md:col-span-4",
      title: "Один человек отвечает за результат",
      text: "Говоришь напрямую со мной, а не с менеджером, который перескажет дальше.",
      visual: <Author />,
    },
  ];

  return (
    <ul className="grid gap-4 md:grid-cols-12">
      {cards.map((c, i) => (
        <li key={c.title} className={c.span}>
          <Reveal delay={(i % 3) * 0.08} className="h-full">
            <Surface className="flex h-full flex-col p-6 sm:p-8">
              <h3 className={c.big ? "h3 text-2xl sm:text-3xl" : "h3 text-xl sm:text-2xl"}>{c.title}</h3>
              <p className="mt-3 text-base text-muted">{c.text}</p>
              <div className="mt-auto pt-8">{c.visual}</div>
            </Surface>
          </Reveal>
        </li>
      ))}
    </ul>
  );
};
